import { Tag, type Line, type Song } from 'chordsheetjs';
import type { HeaderHit } from './preNormalize.ts';
import type { SectionKind } from './types.ts';

// Krok (C) potoku: segmentacja WŁASNA, świadomie obok API biblioteki.
//
// Obchodzi pułapkę 2: `Song.linesToParagraphs` tworzy nowy akapit na każdej
// pustej linii, więc jedna zwrotka z pustą linią w środku rozpadłaby się na
// kilka akapitów. Dlatego tniemy wyłącznie na `song.lines`, nigdy na
// `song.paragraphs`: cięcie następuje TYLKO na tagach startu sekcji
// (`line.isSectionStart()`) i na komentarzach. Puste linie, `end_of_*` i linie
// typu `none` są wchłaniane do bieżącej sekcji, nie zaczynają nowej.
//
// Prawdziwy rodzaj sekcji (`kind`) bierze się z tabeli `HeaderHit` zwróconej
// przez `preNormalize`, NIE z `Line.type` biblioteki: `UltimateGuitarParser`
// spłaszcza intro/outro/instrumental/interlude/solo/pre-chorus do jednego typu
// `PART`, więc sam tag biblioteki nie wystarcza do odróżnienia "Instrumental"
// od "Intro". Złączenie z tabelą `HeaderHit` następuje po kolejności
// wystąpienia: n-ta linia `isSectionStart()` w `song.lines` odpowiada n-temu
// wpisowi w `headers`. Ta zgodność jest sprawdzana jawnie przy starcie
// (`assertHeaderAlignment`) i segment RZUCA, gdy się rozjedzie, zamiast po
// cichu przypisać zły nagłówek do złej sekcji, to jest dokładnie ta krucha
// korelacja, którą audyt wskazał jako ryzyko.

/** Jedna surowa sekcja, przed zbudowaniem progresji i przed scalaniem. */
export interface RawSection {
  /** Linia z tagiem startu sekcji, jeśli sekcja zaczęła się od tagu, inaczej null. */
  headerLine: Line | null;
  /** Wszystkie linie treści należące do tej sekcji, w kolejności oryginalnej. */
  lines: readonly Line[];
  /** Komentarz poprzedzający sekcję (np. `[Zwrotka 1]` po normalizacji na komentarz), jeśli był. */
  comment: string | null;
  /** Prawdziwy rodzaj sekcji, wzięty z `HeaderHit` (albo `other` dla komentarzy/fallbacku). */
  kind: SectionKind;
  /** Etykieta wyświetlana w interfejsie, np. "Z1", "R", "Most", "Część 1". */
  label: string;
  /** Numer sekcji (np. zwrotki), gdy znany, inaczej null. */
  number: number | null;
  /** Powtórzenie zapisane wprost przy nagłówku (np. "Chorus x2" → 2), inaczej null. */
  times: number | null;
  /** true, gdy nagłówek tej sekcji rozpoznano przez tolerancję literówek. */
  isTypo: boolean;
  /** Dopisek opisowy po nagłówku, np. "[Middle] (HARMONICA SOLO)" → "HARMONICA SOLO",
   * przeniesiony bez zmian z `HeaderHit.description`. `null` dla sekcji bez dopisku
   * i dla sekcji z komentarza (nie z prawdziwego nagłówka). */
  description: string | null;
  /** true, gdy WEWNĄTRZ tej sekcji wycięto choć jedną linię tabulatury nutowej,
   * przeniesione z `HeaderHit.hadTabLinesRemoved`. `compile.ts` używa tego, żeby
   * `TAB_ONLY_SECTION` nie trafiało na sekcję po prostu pustą, patrz `TAB_ONLY_SECTION`
   * w `types.ts`. */
  hadTabLinesRemoved: boolean;
}

/** Sekcja odrzucona, bo po wszystkich wycięciach nie miała ani jednego akordu
 * ani jednego słowa tekstu. Realny przypadek: `perfect.txt` ma `[Coda]` jako
 * fałszywy nagłówek w środku tabeli chwytów (patrz komentarz przy prelude
 * niżej - w tym akurat pliku sprawę załatwia granica preambuły, ale sekcja
 * PO pierwszym prawdziwym nagłówku, która okaże się pusta z tego samego
 * powodu w innym pliku, trafi tutaj). Diagnostyka `EMPTY_SECTION` (poziom
 * `info`) należy do `compile.ts`, poza zakresem tego modułu - to jest surowy
 * fakt, z którego `compile.ts` ją zbuduje. */
export interface DroppedEmptySection {
  kind: SectionKind;
  label: string;
  comment: string | null;
}

/** Sekcja zdjęta jako blok INSTRUKTAŻOWY, patrz {@link stripInstructionalBlocks}. */
export interface DroppedInstructionalSection {
  kind: SectionKind;
  label: string;
}

export interface SegmentResult {
  /** Wszystko przed pierwszym PRAWDZIWIE rozpoznanym nagłówkiem sekcji (czyli
   * przed pierwszą linią `isSectionStart()`). Nigdy nie tworzy sekcji.
   * Nierozpoznane nawiasy (komentarze biblioteki) STOJĄCE W TEJ STREFIE nie
   * kończą preambuły, patrz komentarz w `segment()`. */
  prelude: readonly Line[];
  sections: RawSection[];
  /** Sekcje pominięte, bo okazały się puste po wycięciach, patrz {@link DroppedEmptySection}. */
  droppedEmpty: readonly DroppedEmptySection[];
  /** Sekcje zdjęte jako bloki instruktażowe, patrz {@link stripInstructionalBlocks}. */
  droppedInstructional: readonly DroppedInstructionalSection[];
  /** Dosłowna treść uwag autora PO ostatniej sekcji, patrz {@link extractClosingNotes}.
   * `null`, gdy taki zamykający akapit nie istnieje. */
  closingNotes: string | null;
}

const HEADER_KIND_TO_SECTION_KIND: Record<HeaderHit['kind'], SectionKind> = {
  intro: 'intro',
  verse: 'verse',
  prechorus: 'prechorus',
  chorus: 'chorus',
  bridge: 'bridge',
  outro: 'outro',
  instrumental: 'instrumental',
  solo: 'solo',
  interlude: 'interlude',
};

const SIMPLE_LABEL: Partial<Record<SectionKind, string>> = {
  chorus: 'R',
  prechorus: 'P',
  bridge: 'Most',
  outro: 'O',
  intro: 'I',
  solo: 'Solo',
  interlude: 'Int',
  instrumental: 'Instr',
};

function isCommentLine(line: Line): string | null {
  if (line.items.length !== 1) return null;
  const [item] = line.items;
  if (!(item instanceof Tag) || !item.isComment()) return null;
  return item.value;
}

/**
 * Buduje etykietę sekcji z jej rodzaju i numeru.
 *
 * Zwrotki bez numeru podanego przez autora (np. "Verse:" bez cyfry) dostają
 * numer z rosnącego licznika, NIEZALEŻNEGO od numerów podanych jawnie przez
 * autora gdzie indziej w tym samym pliku (np. "2nd:"). Realny przypadek z
 * korpusu: `nie-placz-ewka.txt` ma "Verse:" (bez numeru, dostaje 1 z licznika)
 * i później "2nd:" (jawne 2).
 *
 * Pozostałe rodzaje NIGDY nie dostają numeru z licznika automatycznego -
 * numer bierzemy wyłącznie wtedy, gdy autor go podał wprost w nagłówku (np.
 * "[Chorus 1]"/"[Chorus 2]" w `shape-of-my-heart.txt` → "R1"/"R2"). Bez
 * jawnego numeru zostaje zwykła krótka etykieta ("R", "Most"), tak jak dotąd:
 * dopisywanie numeru „z automatu” refrenom zepsułoby siedem polskich plików
 * korpusu, gdzie wielokrotne, nienumerowane [Chorus] mają dawać to samo "R".
 */
function buildLabel(
  kind: SectionKind,
  hit: HeaderHit,
  nextAutoVerseNumber: { value: number },
): string {
  if (kind === 'verse') {
    if (hit.number !== null) return `Z${hit.number}`;
    const n = nextAutoVerseNumber.value;
    nextAutoVerseNumber.value += 1;
    return `Z${n}`;
  }
  const base = SIMPLE_LABEL[kind] ?? hit.label;
  return hit.number !== null ? `${base}${hit.number}` : base;
}

interface PendingHeader {
  kind: 'header';
  hit: HeaderHit;
}

interface PendingComment {
  kind: 'comment';
  text: string;
}

type Pending = PendingHeader | PendingComment | null;

function buildSection(
  pending: Pending,
  lines: readonly Line[],
  headerLine: Line | null,
  nextAutoVerseNumber: { value: number },
): RawSection {
  if (pending === null) {
    throw new Error('segment: próba zbudowania sekcji bez nagłówka ani komentarza');
  }
  if (pending.kind === 'comment') {
    return {
      headerLine: null,
      lines,
      comment: pending.text,
      kind: 'other',
      label: pending.text,
      number: null,
      times: null,
      isTypo: false,
      description: null,
      hadTabLinesRemoved: false,
    };
  }
  const { hit } = pending;
  const kind = HEADER_KIND_TO_SECTION_KIND[hit.kind];
  const label = buildLabel(kind, hit, nextAutoVerseNumber);
  return {
    headerLine,
    lines,
    comment: null,
    kind,
    label,
    number: hit.number,
    times: hit.times,
    isTypo: hit.isTypo,
    description: hit.description,
    hadTabLinesRemoved: hit.hadTabLinesRemoved,
  };
}

/** true, gdy żadna linia nie niesie ani jednego akordu, ani jednego niepustego
 * słowa tekstu - czyli sekcja, którą warto porzucić zamiast pokazywać pustą
 * ramkę bez treści. Eksportowana, bo `compile.ts` używa jej do wykrycia sekcji
 * `tabOnly` (ogólna reguła, patrz `TAB_ONLY_SECTION` w `types.ts`, ZAMIAST listy
 * nazw plików: sekcja z prawdziwym nagłówkiem, której `segment()` NIE odrzuca,
 * a która i tak nie ma ani akordów, ani tekstu, jest w praktyce sekcją zapisaną
 * wyłącznie tabulaturą, którą `preNormalize` już wyciął). */
export function isContentEmpty(lines: readonly Line[]): boolean {
  for (const line of lines) {
    for (const item of line.items) {
      if ('chords' in item && typeof item.chords === 'string' && item.chords.trim() !== '') return false;
      if ('lyrics' in item && typeof item.lyrics === 'string' && item.lyrics.trim() !== '') return false;
    }
  }
  return true;
}

/** Rodzaje sekcji, które z nazwy są ŚPIEWANE. Sekcja instrumentalna, intro, outro,
 * solo czy interlude z definicji może nie mieć ani słowa tekstu i to jest normalne,
 * więc reguła bloku instruktażowego ich NIE dotyczy. */
const SUNG_KINDS: ReadonlySet<SectionKind> = new Set<SectionKind>([
  'verse', 'prechorus', 'chorus', 'bridge',
]);

function hasLyrics(lines: readonly Line[]): boolean {
  for (const line of lines) {
    for (const item of line.items) {
      if ('lyrics' in item && typeof item.lyrics === 'string' && item.lyrics.trim() !== '') return true;
    }
  }
  return false;
}

/**
 * Zdejmuje z POCZĄTKU utworu bloki INSTRUKTAŻOWE, czyli sekcje o nazwie części
 * śpiewanej ("Verse:", "Chorus:"), które nie mają ani jednego słowa tekstu i
 * stoją przed pierwszą sekcją, która tekst ma. Taki blok nie jest przebiegiem
 * utworu, tylko pokazem wzoru gry: autor rysuje arpeggio w tabulaturze z
 * akordami nad nią i podpisuje "tak gra się zwrotkę".
 *
 * Realny przypadek i powód istnienia reguły: `nie-placz-ewka.txt` otwiera się
 * blokiem "Verse:" z sześciostrunową tabulaturą i akordami A F#m E E, bez
 * jednego słowa. Utwór zaczyna się dopiero od "[Intro]" w linii 61, a pierwsza
 * zwrotka stoi pod "1st:" w linii 67. Bez tej reguły skrót mówiłby, że utwór
 * zaczyna się od zwrotki, a prawdziwa pierwsza zwrotka wpadałaby do intro.
 *
 * Rozstrzyganie idzie po TYM, CO MÓWI NAGŁÓWEK, nie po pozycji w pliku, i to
 * jest sedno. Reguła oparta na samej pozycji ("blok tabulaturowy przed pierwszą
 * sekcją z tekstem") zjadłaby intro `wehikul-czasu.txt`, które też jest samą
 * tabulaturą bez słowa, ale jest prawdziwym intrem utworu. "Intro" nie jest
 * częścią śpiewaną, więc nie przechodzi przez {@link SUNG_KINDS} i zostaje.
 *
 * Warunek bezpieczeństwa: musi istnieć PÓŹNIEJSZA sekcja z tekstem. W utworze
 * czysto instrumentalnym żaden blok nie jest instruktażowy i nie wolno usunąć
 * całej treści.
 */
function stripInstructionalBlocks(
  sections: RawSection[],
  prelude: Line[],
): DroppedInstructionalSection[] {
  const firstWithLyrics = sections.findIndex((s) => hasLyrics(s.lines));
  if (firstWithLyrics <= 0) return [];

  let cut = 0;
  while (cut < firstWithLyrics) {
    const section = sections[cut]!;
    if (!SUNG_KINDS.has(section.kind) || hasLyrics(section.lines)) break;
    cut += 1;
  }
  if (cut === 0) return [];

  const removed = sections.splice(0, cut);
  for (const section of removed) prelude.push(...section.lines);

  // Numeracja automatyczna zwrotek liczyła też zdjęte bloki, więc po usunięciu
  // trzeba ją przeliczyć. Zwrotki z numerem podanym przez autora (`number`
  // niepuste, np. "2nd:") zostają nietknięte, tak jak przed zmianą.
  let auto = 1;
  for (const section of sections) {
    if (section.kind === 'verse' && section.number === null) {
      section.label = `Z${auto}`;
      auto += 1;
    }
  }

  return removed.map((s) => ({ kind: s.kind, label: s.label }));
}

/** Podgląd etykiety porzucanej sekcji, do diagnostyki - NIE korzysta z licznika
 * automatycznego numerowania zwrotek (skoro sekcja i tak znika, nie ma czego
 * numerować), stąd `hit.label` z `preNormalize` wprost, nie `buildLabel`. */
function previewDroppedLabel(pending: Pending): DroppedEmptySection {
  if (pending === null) return { kind: 'other', label: '', comment: null };
  if (pending.kind === 'comment') return { kind: 'other', label: pending.text, comment: pending.text };
  const kind = HEADER_KIND_TO_SECTION_KIND[pending.hit.kind];
  return { kind, label: pending.hit.label, comment: null };
}

function lineHasChord(line: Line): boolean {
  return line.items.some((item) => 'chords' in item && typeof item.chords === 'string' && item.chords.trim() !== '');
}

function lineLyricsText(line: Line): string {
  let out = '';
  for (const item of line.items) {
    if ('lyrics' in item && typeof item.lyrics === 'string') out += item.lyrics;
  }
  return out;
}

/**
 * Wyodrębnia z KOŃCA sekcji uwagi końcowe autora, czyli ostatni akapit (ciąg
 * niepustych linii, oddzielony od reszty choć jedną pustą linią), który nie
 * ma ANI JEDNEGO akordu, podczas gdy WCZEŚNIEJ w tej samej sekcji jakiś akord
 * już był. Realny przypadek i powód istnienia reguły: `shape-of-my-heart.txt`
 * kończy się piątą zwrotką (akordy jak zwykle), a PO NIEJ, oddzielona dwiema
 * pustymi liniami, stoi angielska proza "CHORUS 2, REPEAT CHORDS AS NORMAL...
 * / END ON Em" - instrukcja, że utwór gra się dalej, niż sięga ostatni
 * nagłówek.
 *
 * Kryterium jest OGÓLNE, nie dopasowane do jednego pliku: odróżnia zwykły
 * końcowy wers piosenki (który stoi w TYM SAMYM akapicie co ostatni akord,
 * więc akapitów w tej sekcji jest jeden, a warunek `boundaries.length < 2`
 * niżej go w ogóle nie rusza) od odrębnej prozy DOŁOŻONEJ po muzyce (osobny
 * akapit, zero akordów, ale sekcja jako całość akordy miała). Sama pustka
 * akordów w ostatnim akapicie NIE wystarcza - czysto instrumentalna końcówka
 * (kolejny takt bez śpiewu) nie ma żadnego tekstu i odpada przez warunek
 * `lastHasLyrics`.
 *
 * Zwraca sekcję OKROJONĄ o ten akapit (żeby proza nie trafiała do progresji
 * ani do kotwicy) razem z jej dosłowną treścią, złożoną z oryginalnych
 * `lyrics` linii akapitu (bez parafrazy, ze wszystkimi spacjami wcięcia).
 */
function extractClosingNotes(lines: readonly Line[]): { lines: readonly Line[]; notes: string | null } {
  const paragraphStarts: number[] = [];
  let inParagraph = false;
  lines.forEach((line, i) => {
    if (line.isEmpty()) {
      inParagraph = false;
      return;
    }
    if (!inParagraph) {
      paragraphStarts.push(i);
      inParagraph = true;
    }
  });

  if (paragraphStarts.length < 2) return { lines, notes: null };

  const lastStart = paragraphStarts[paragraphStarts.length - 1]!;
  const lastParagraph = lines.slice(lastStart).filter((l) => !l.isEmpty());
  const earlierLines = lines.slice(0, lastStart);

  const lastHasChord = lastParagraph.some(lineHasChord);
  const earlierHasChord = earlierLines.some(lineHasChord);
  const lastHasLyrics = lastParagraph.some((l) => lineLyricsText(l).trim() !== '');

  if (lastHasChord || !earlierHasChord || !lastHasLyrics) return { lines, notes: null };

  const notes = lastParagraph.map(lineLyricsText).join('\n');
  return { lines: earlierLines, notes };
}

/** Dzieli po pustych liniach, z etykietami "Część 1..N". Używane WYŁĄCZNIE gdy
 * w całym pliku nie ma ani jednego rozpoznanego nagłówka sekcji. W tym trybie
 * nie ma preambuły: pierwsza grupa treści jest zwykłą pierwszą częścią. */
function segmentByBlankLines(lines: readonly Line[]): SegmentResult {
  const sections: RawSection[] = [];
  let current: Line[] = [];
  let index = 0;

  const flush = () => {
    if (current.length === 0) return;
    index += 1;
    sections.push({
      headerLine: null,
      lines: current,
      comment: null,
      kind: 'other',
      label: `Część ${index}`,
      number: index,
      times: null,
      isTypo: false,
      description: null,
      hadTabLinesRemoved: false,
    });
    current = [];
  };

  for (const line of lines) {
    if (line.isEmpty()) {
      flush();
      continue;
    }
    current.push(line);
  }
  flush();

  let closingNotes: string | null = null;
  const lastSection = sections[sections.length - 1];
  if (lastSection) {
    const extracted = extractClosingNotes(lastSection.lines);
    lastSection.lines = extracted.lines;
    closingNotes = extracted.notes;
  }

  return { prelude: [], sections, droppedEmpty: [], droppedInstructional: [], closingNotes };
}

/**
 * Dzieli `song.lines` na surowe sekcje, tnąc wyłącznie na tagach startu sekcji
 * i na komentarzach. Puste linie i linie bez wyraźnego typu zostają wchłonięte
 * do bieżącej sekcji, więc zwrotka z pustą linią w środku pozostaje jedną sekcją.
 *
 * `headers` to tabela z `preNormalize`, w kolejności występowania, jeden wpis
 * na każdą linię `isSectionStart()` w `song`. Bez zgodności liczności segment
 * rzuca, zamiast zgadywać złączenie.
 *
 * Komentarze (nierozpoznane nawiasy typu `[Coda]`, zamieniane przez bibliotekę
 * na tag `{comment}`) kończą preambułę i zaczynają nową sekcję TYLKO wtedy,
 * gdy pojawiają się PO pierwszym prawdziwym nagłówku (`isSectionStart()`).
 * Zanim padnie pierwszy prawdziwy nagłówek, komentarz jest jeszcze szumem
 * preambuły, nie zapowiedzią sekcji: plan mówi wprost "wszystko przed
 * PIERWSZYM ROZPOZNANYM nagłówkiem trafia do prelude", a nierozpoznany nawias
 * z definicji nie jest rozpoznanym nagłówkiem. Realny przypadek: `perfect.txt`
 * ma fałszywy nagłówek `[Coda]` wewnątrz tabeli chwytów w preambule, długo
 * przed pierwszym prawdziwym `[Intro]`. Bez tej reguły `[Coda]` wraz z
 * towarzyszącym mu akapitem wyjaśniającym notację "%" dostałby własną,
 * fałszywą sekcję eleventh, zamiast zniknąć w preambule razem z resztą
 * materiału wprowadzającego.
 *
 * Sekcja (header albo comment), która po wszystkich wycięciach nie ma ani
 * jednego akordu, ani jednego słowa tekstu, NIE POWSTAJE - trafia do
 * `droppedEmpty` zamiast do `sections`. To osobna siatka bezpieczeństwa od
 * powyższej: obsługuje przypadek, gdy fałszywy nagłówek/komentarz stoi W
 * ŚRODKU utworu (po pierwszym prawdziwym nagłówku), gdzie reguła preambuły
 * już nie chroni.
 */
export function segment(song: Song, headers: readonly HeaderHit[]): SegmentResult {
  const startLineCount = song.lines.filter((line) => line.isSectionStart()).length;
  if (startLineCount !== headers.length) {
    throw new Error(
      `segment: liczba tagów startu sekcji w Song (${startLineCount}) nie zgadza się z tabelą `
      + `nagłówków z preNormalize (${headers.length}). Złączenie po kolejności wystąpień jest `
      + 'niepewne, więc nie zgaduję.',
    );
  }

  if (headers.length === 0) {
    return segmentByBlankLines(song.lines);
  }

  const prelude: Line[] = [];
  const sections: RawSection[] = [];
  const droppedEmpty: DroppedEmptySection[] = [];
  const nextAutoVerseNumber = { value: 1 };

  let current: Line[] = [];
  let pending: Pending = null;
  let pendingHeaderLine: Line | null = null;
  let sawRealHeader = false;
  let headerCursor = 0;

  const flush = () => {
    if (!sawRealHeader) {
      prelude.push(...current);
    } else if (pending !== null && pending.kind === 'comment' && isContentEmpty(current)) {
      // Porzucanie ograniczone do sekcji z KOMENTARZA (nierozpoznany nawias),
      // nigdy z prawdziwego nagłówka rodzinnego (`isSectionStart()`): prawdziwy
      // nagłówek bez własnej treści to legalny, zamierzony przypadek (np.
      // zwrotka dziedzicząca progresję, albo goły instrumentalny tag), a
      // fałszywy nawias bez żadnej treści (jak "[Coda]" w tabeli chwytów) jest
      // niemal na pewno śmieciem, nie zamierzoną strukturą utworu.
      droppedEmpty.push(previewDroppedLabel(pending));
    } else {
      sections.push(buildSection(pending, current, pendingHeaderLine, nextAutoVerseNumber));
    }
    current = [];
  };

  for (const line of song.lines) {
    if (line.isSectionStart()) {
      flush();
      const hit = headers[headerCursor];
      if (hit === undefined) {
        throw new Error('segment: zabrakło wpisu w tabeli nagłówków, mimo zgodnej liczności');
      }
      headerCursor += 1;
      pending = { kind: 'header', hit };
      pendingHeaderLine = line;
      sawRealHeader = true;
      continue;
    }

    const commentText = isCommentLine(line);
    if (commentText !== null && sawRealHeader) {
      flush();
      pending = { kind: 'comment', text: commentText };
      pendingHeaderLine = null;
      continue;
    }

    current.push(line);
  }
  flush();

  const droppedInstructional = stripInstructionalBlocks(sections, prelude);

  let closingNotes: string | null = null;
  const lastSection = sections[sections.length - 1];
  if (lastSection) {
    const extracted = extractClosingNotes(lastSection.lines);
    lastSection.lines = extracted.lines;
    closingNotes = extracted.notes;
  }

  return { prelude, sections, droppedEmpty, droppedInstructional, closingNotes };
}
